/**
 * Matrix authoring catalog + resolver — UAM Custom Roles v2.0
 *
 * The matrix is a frontend authoring layer. It is NOT stored on the role
 * document — only the resolved permissions array persists. The Role Editor
 * uses this module to:
 *   1. Render rows + dropdowns
 *   2. Convert matrix selections to a flat permissions array on save
 *      (`resolveMatrixToPermissions`)
 *   3. Reverse-resolve a stored permissions array back into matrix
 *      selections when an existing role is opened for edit
 *      (`reverseResolvePermissions`)
 *
 * v2.0 changes (Phase 1):
 *  - Two-row Employees: Manage Employee + Sensitive Info, each None/View/Edit
 *  - Sensitive Info ≥ View auto-bumps Manage Employee ≥ View
 *  - Kiosk row removed
 *  - dash:* derivations removed (cards gate directly on area perms; the
 *    Dashboard route is accessible to any authenticated user)
 *
 * See PRD_UAM_CustomRoles2.md §4 (matrix specs).
 */

import { PERMISSIONS } from './permissions';

// ============================================================
// MATRIX DEFINITION
// ============================================================
//
// Cell shape:
//   levels: {
//     view?: { grants: string[] }            // grants when level === 'view'
//     edit?: { grants: string[]; autoBumps? }  // additionally granted when 'edit'
//   }
// `autoBumps`: a map of `{ [targetSubItemId]: 'view' | 'edit' }`. When this
// cell is selected, the dependent row is bumped up to (at least) the named
// level.
//
// Some sub-items omit View (action-only pages: Process Payroll) or Edit
// (view-only pages: Payroll List).

export const MATRIX_ROWS = [
  {
    id: 'employees',
    label: 'Employees',
    subItems: [
      {
        id: 'employees.manage',
        label: 'Manage Employee',
        levels: {
          view: { grants: [PERMISSIONS.EMPLOYEES_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.EMPLOYEES_VIEW,
              PERMISSIONS.EMPLOYEES_EDIT,
            ],
          },
        },
      },
      {
        id: 'employees.sensitive',
        label: 'Sensitive Info',
        levels: {
          view: {
            grants: [PERMISSIONS.EMPLOYEES_PII_VIEW],
            // PII access is meaningless without the parent employee record
            // being readable — auto-bump Manage Employee to ≥ View. The
            // resolver applies this transitively, so a "Sensitive=View"
            // selection alone resolves to [emp:view, emp:pii:view].
            autoBumps: { 'employees.manage': 'view' },
          },
          edit: {
            grants: [
              PERMISSIONS.EMPLOYEES_PII_VIEW,
              PERMISSIONS.EMPLOYEES_PII_EDIT,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
        },
      },
      {
        id: 'employees.schedule',
        label: 'Work Schedule',
        // The Work Schedule page renders employee names and departments, so
        // emp:view is bundled at BOTH levels and Manage Employee floors at
        // View — never employees.sensitive (the scheduler must not see
        // compensation). No row bumps THIS row: a Manage Employee = Edit →
        // Work Schedule = Edit back-edge would be the matrix's first mutual
        // auto-bump and gives deriveFloors a circular badge
        // (PRD_WorkScheduleManagement.md C1; C13 withdrawn).
        levels: {
          view: {
            grants: [
              PERMISSIONS.SCHEDULE_VIEW,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
          edit: {
            grants: [
              PERMISSIONS.SCHEDULE_VIEW,
              PERMISSIONS.SCHEDULE_EDIT,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
        },
      },
      {
        id: 'employees.loans',
        label: 'Loans',
        // Same shape as Work Schedule: the Loans page renders employee names
        // and departments, so emp:view is bundled at BOTH levels and Manage
        // Employee floors at View — never employees.sensitive (a loans clerk
        // must not see compensation). No back-edge from Manage Employee
        // (PRD_LoansManagement.md C11).
        levels: {
          view: {
            grants: [
              PERMISSIONS.LOANS_VIEW,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
          edit: {
            grants: [
              PERMISSIONS.LOANS_VIEW,
              PERMISSIONS.LOANS_EDIT,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
        },
      },
    ],
  },
  {
    id: 'time-attendance',
    label: 'Time & Attendance',
    subItems: [
      {
        id: 'time.tracking',
        label: 'Time Tracking',
        levels: {
          view: {
            grants: [
              PERMISSIONS.TIME_TRACKING_VIEW,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            // The page lists employees by name/department, so emp:view is
            // bundled in the grants. Surface that in the UI by bumping
            // Manage Employee — keeps live preview honest about what the
            // user can see.
            autoBumps: { 'employees.manage': 'view' },
          },
          edit: {
            grants: [
              PERMISSIONS.TIME_TRACKING_VIEW,
              PERMISSIONS.TIME_TRACKING_EDIT,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
        },
      },
      {
        id: 'time.ot-approval',
        label: 'OT Approval',
        levels: {
          view: {
            grants: [
              PERMISSIONS.TIME_TRACKING_VIEW,
              PERMISSIONS.TIME_OT_VIEW,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            // OT pages also display attendance records, so time:view is
            // bundled. Bumping Time Tracking cascades to Manage Employee
            // via the time.tracking autoBump above. applyAutoBumps now
            // iterates to fixed-point to handle this.
            autoBumps: { 'time.tracking': 'view' },
          },
          edit: {
            grants: [
              PERMISSIONS.TIME_TRACKING_VIEW,
              PERMISSIONS.TIME_OT_VIEW,
              PERMISSIONS.TIME_OT_EDIT,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'time.tracking': 'view' },
          },
        },
      },
      {
        id: 'time.kiosks',
        label: 'Kiosks',
        levels: {
          view: { grants: [PERMISSIONS.KIOSK_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.KIOSK_VIEW,
              PERMISSIONS.KIOSK_EDIT,
            ],
          },
        },
      },
    ],
  },
  {
    id: 'payroll',
    label: 'Payroll',
    subItems: [
      {
        id: 'payroll.list',
        label: 'Payroll List',
        levels: {
          view: {
            grants: [
              PERMISSIONS.PAYROLL_VIEW,
              PERMISSIONS.PAYSLIP_VIEW,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            // Payroll List displays employee names from
            // each run's embedded snapshot, but the page chrome (and any
            // employee-lookup affordances) still expect emp:view.
            autoBumps: { 'employees.manage': 'view' },
          },
        },
      },
      {
        id: 'payroll.adjustments',
        label: 'Adjustments',
        levels: {
          // The Adjustments page renders an employee column and its picker
          // reads employee names/departments, so emp:view is bundled at BOTH
          // levels (same pattern as time.tracking / payroll.list). Never
          // employees.sensitive — the adjustments clerk must not see
          // compensation (PRD_StandaloneAdjustments C8).
          view: {
            grants: [
              PERMISSIONS.ADJUSTMENTS_VIEW,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
          edit: {
            grants: [
              PERMISSIONS.ADJUSTMENTS_VIEW,
              PERMISSIONS.ADJUSTMENTS_EDIT,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            autoBumps: { 'employees.manage': 'view' },
          },
        },
      },
      {
        id: 'payroll.process',
        label: 'Process Payroll',
        levels: {
          edit: {
            grants: [
              PERMISSIONS.PAYROLL_VIEW,
              PERMISSIONS.PAYROLL_RUN,
              PERMISSIONS.EMPLOYEES_VIEW,
            ],
            // ProcessPayroll reads PII (compensation, banking) and DTRs
            // (hours worked) per employee to compute the run. (DTR locks are
            // now derived from the payroll index — PRD_DtrWriteModel C1 — so the
            // run no longer writes lock metadata to dailyTimeRecords.) Without
            // sensitive=view (→ emp:pii:view), the PII fetch is denied; without
            // time.tracking=EDIT (→ time:view + time:edit), the DTR fetch fails.
            //
            // time.tracking is bumped to EDIT rather than VIEW because
            // the Payroll Specialist persona is trusted to correct DTR
            // issues during payroll prep. Real-world workflow: missing
            // clock-outs, holiday misclassifications, etc. surface during
            // payroll review and would otherwise require handing off to
            // a DTR encoder mid-run. See PRD §4.4 for the rationale and
            // separation-of-duties trade-off.
            //
            // PRD §4.2 originally documented only emp:view for this cell —
            // the table assumed a processPayroll Cloud Function would read
            // PII/DTRs and write locks server-side. With the CF not
            // implemented, those operations happen client-side and require
            // the perms directly.
            autoBumps: {
              'employees.manage': 'view',
              'employees.sensitive': 'view',
              'time.tracking': 'edit',
              // The payroll runner manages adjustments inside the wizard's Adjustments step (create /
              // edit / remove / void), so Process Payroll floors the
              // Adjustments row at Edit (PRD_StandaloneAdjustments C7).
              'payroll.adjustments': 'edit',
            },
          },
        },
      },
      {
        id: 'payroll.approval',
        label: 'Payroll Approval',
        // Action-only page (no payroll:approve:view variant exists) →
        // Edit-only, renders None / — / Edit like Process Payroll.
        levels: {
          edit: {
            grants: [
              PERMISSIONS.PAYROLL_APPROVE,   // gate the route + approve/reject actions
              PERMISSIONS.PAYROLL_VIEW,      // read payrollRuns on the approval page
            ],
            // Approving writes payroll runs + the index (payroll:run) and DTR
            // lock/deduction fields (time:edit). Express that dependency the
            // idiomatic way — auto-bump Process Payroll = Edit. The fixed-point
            // resolver then cascades payroll.process's own auto-bumps
            // (sensitive:view, time.tracking:edit, manage:view), yielding the
            // full processing+approval persona. Per-run separation of duties is
            // still enforced server-side: the approve transaction blocks a user
            // from approving their own submission under maker-checker policy.
            autoBumps: { 'payroll.process': 'edit' },
          },
        },
      },
    ],
  },
  {
    id: 'settings',
    label: 'Settings',
    // Sub-item order mirrors the actual Settings sidebar
    // (Company group: Departments → Team → Roles → Attendance Policy → Holidays,
    //  Payroll group: Pay Rates → CSV Export → Payslip Format) so admins see
    // matrix rows in the same sequence they navigate the live app.
    subItems: [
      {
        id: 'settings.dept',
        label: 'Departments',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_DEPARTMENTS,
              PERMISSIONS.SETTINGS_DEPARTMENTS_VIEW,
            ],
          },
        },
      },
      {
        id: 'settings.team',
        label: 'Team',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_TEAM_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_TEAM_VIEW,
              PERMISSIONS.SETTINGS_TEAM_EDIT,
            ],
          },
        },
      },
      {
        id: 'settings.roles',
        label: 'Roles',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_ROLES_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_ROLES_VIEW,
              PERMISSIONS.SETTINGS_ROLES_EDIT,
            ],
          },
        },
      },
      {
        id: 'settings.attPolicy',
        label: 'Attendance Policy',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_ATTENDANCE_POLICY,
              PERMISSIONS.SETTINGS_ATTENDANCE_POLICY_VIEW,
            ],
          },
        },
      },
      {
        id: 'settings.holidays',
        label: 'Holidays',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_HOLIDAYS_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_HOLIDAYS,
              PERMISSIONS.SETTINGS_HOLIDAYS_VIEW,
            ],
          },
        },
      },
      {
        id: 'settings.rates',
        label: 'Pay Rates',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_PAYROLL_RATES,
              PERMISSIONS.SETTINGS_PAYROLL_RATES_VIEW,
            ],
          },
        },
      },
      {
        id: 'settings.csv',
        label: 'CSV Export',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_CSV_EXPORT,
              PERMISSIONS.SETTINGS_CSV_EXPORT_VIEW,
            ],
          },
        },
      },
      {
        id: 'settings.payslips',
        label: 'Payslip Format',
        levels: {
          view: { grants: [PERMISSIONS.SETTINGS_PAYSLIPS_VIEW] },
          edit: {
            grants: [
              PERMISSIONS.SETTINGS_PAYSLIPS,
              PERMISSIONS.SETTINGS_PAYSLIPS_VIEW,
            ],
          },
        },
      },
    ],
  },
];

export const ALL_SUB_ITEMS = MATRIX_ROWS.flatMap((row) => row.subItems);

const LEVEL_ORDER = { none: 0, view: 1, edit: 2 };

// ============================================================
// FORWARD RESOLUTION — selections → permissions[]
// ============================================================

/**
 * Pick the higher of two levels (none < view < edit).
 */
export function highestLevel(a, b) {
  return LEVEL_ORDER[a] >= LEVEL_ORDER[b] ? a : b;
}

/**
 * Apply each cell's `autoBumps` map to the selections, raising dependent
 * rows to (at least) the bumped level. Returns a new selections object.
 *
 * Iterates to a fixed point to handle transitive cascades — e.g. OT
 * Approval = View bumps Time Tracking = View, which then bumps Manage
 * Employee = View on its own autoBump. Single-pass iteration would miss
 * the second hop because Time Tracking comes before OT Approval in the
 * sub-item order. Worst-case depth is ≤ 3 (OT Approval → Time Tracking
 * → Manage Employee); the loop terminates as soon as no level changes.
 */
export function applyAutoBumps(selections) {
  const result = { ...selections };
  let changed = true;
  while (changed) {
    changed = false;
    for (const subItem of ALL_SUB_ITEMS) {
      const level = result[subItem.id];
      const cell = level ? subItem.levels[level] : null;
      if (cell && cell.autoBumps) {
        for (const [targetId, targetLevel] of Object.entries(cell.autoBumps)) {
          const current = result[targetId] || 'none';
          const next = highestLevel(current, targetLevel);
          if (next !== current) {
            result[targetId] = next;
            changed = true;
          }
        }
      }
    }
  }
  return result;
}

/**
 * Derive, for each sub-item, the minimum ("floor") level that OTHER rows'
 * autoBumps force onto it — and WHICH rows force it. This is the same
 * fixed-point cascade as applyAutoBumps, but it records the source of each
 * bump instead of only the result, so the Role Editor can EXPLAIN a silent
 * auto-bump ("Minimum: View — required by Process Payroll (Edit)") instead of
 * accepting a click that snaps back with no message.
 *
 * Read-only reporting: it does NOT change selections or what a role grants —
 * it only surfaces the constraints applyAutoBumps already imposes. Sources are
 * the DIRECT forcer at fixed point (e.g. Payroll Approval forces Process
 * Payroll, which in turn is the direct forcer of Time Tracking).
 *
 * @param {Object<string,string>} selections — { [subItemId]: 'none'|'view'|'edit' }
 * @returns {Object<string, { level: 'view'|'edit', sources: Array<{id:string,label:string,level:string}> }>}
 *   keyed by the forced sub-item id; an absent key means no floor (free to set None).
 */
export function deriveFloors(selections) {
  const effective = applyAutoBumps(selections);
  const labelById = {};
  for (const s of ALL_SUB_ITEMS) labelById[s.id] = s.label;

  const floors = {};
  for (const source of ALL_SUB_ITEMS) {
    const level = effective[source.id];
    const cell = level ? source.levels[level] : null;
    if (!cell || !cell.autoBumps) continue;
    for (const [targetId, targetLevel] of Object.entries(cell.autoBumps)) {
      const src = { id: source.id, label: labelById[source.id], level };
      const existing = floors[targetId];
      if (!existing || LEVEL_ORDER[targetLevel] > LEVEL_ORDER[existing.level]) {
        floors[targetId] = { level: targetLevel, sources: [src] };
      } else if (LEVEL_ORDER[targetLevel] === LEVEL_ORDER[existing.level]) {
        existing.sources.push(src);
      }
    }
  }
  return floors;
}

/**
 * Resolve a floored row's forcing chain down to the row(s) the admin can
 * actually change — the forcer(s) that are NOT themselves floored. For a
 * transitive floor (Payroll Approval=Edit → Process Payroll=Edit → Time
 * Tracking=Edit), Time Tracking's DIRECT forcer (Process Payroll) is itself
 * locked, so telling the admin to "lower Process Payroll" is a dead end — the
 * reachable root to lower is Payroll Approval. This walks up deriveFloors'
 * sources until it reaches un-floored rows. (BL-058)
 *
 * @param {ReturnType<typeof deriveFloors>} floors
 * @param {string} targetId
 * @returns {Array<{id:string,label:string,level:string}>} un-floored root forcers, deduped
 */
export function floorRootSources(floors, targetId) {
  const roots = [];
  const seenRoots = new Set();
  const seenTargets = new Set();
  const visit = (id) => {
    if (seenTargets.has(id)) return; // guard against any cycle
    seenTargets.add(id);
    const f = floors[id];
    if (!f) return;
    for (const s of f.sources) {
      if (floors[s.id]) {
        visit(s.id); // the forcer is itself floored — keep walking up
      } else if (!seenRoots.has(s.id)) {
        seenRoots.add(s.id);
        roots.push(s);
      }
    }
  };
  visit(targetId);
  return roots;
}

/**
 * Resolve matrix selections (and any custom permissions preserved from a
 * previous reverse-resolution) into a flat, deduplicated permissions array.
 *
 * `customPermissions` are permissions that didn't fit any matrix cell when
 * the role was last reverse-resolved. The Role Editor preserves them by
 * union on save so that opening + saving an existing role never silently
 * strips perms.
 *
 * @param {Object<string, string>} selections — { [subItemId]: 'none'|'view'|'edit' }
 * @param {string[]} [customPermissions=[]]
 * @returns {string[]} resolved permission keys, deduplicated
 */
export function resolveMatrixToPermissions(selections, customPermissions = []) {
  const effective = applyAutoBumps(selections);
  const grants = new Set(customPermissions);

  for (const subItem of ALL_SUB_ITEMS) {
    const level = effective[subItem.id];
    if (level === 'none' || level === undefined) continue;
    const cell = subItem.levels[level];
    if (!cell) continue;
    cell.grants.forEach((p) => grants.add(p));
  }

  return Array.from(grants);
}

// ============================================================
// REVERSE RESOLUTION — permissions[] → selections + customPermissions
// ============================================================

/**
 * Best-fit reverse resolution. For each sub-item, picks the highest level
 * whose `grants` array is fully contained in the input permissions. Any
 * permissions that don't fit any cell are returned as `customPermissions`.
 *
 * @param {string[]} permissions
 * @returns {{ selections: Object<string,string>, customPermissions: string[] }}
 */
export function reverseResolvePermissions(permissions) {
  const permsSet = new Set(permissions);
  const selections = {};
  const accountedFor = new Set();

  for (const subItem of ALL_SUB_ITEMS) {
    let chosen = 'none';
    for (const level of ['edit', 'view']) {
      const cell = subItem.levels[level];
      if (!cell) continue;
      if (cell.grants.every((p) => permsSet.has(p))) {
        chosen = level;
        cell.grants.forEach((p) => accountedFor.add(p));
        break;
      }
    }
    selections[subItem.id] = chosen;
  }

  const customPermissions = permissions.filter((p) => !accountedFor.has(p));

  return { selections, customPermissions };
}

/**
 * Count the number of non-None matrix selections in a permissions array.
 * The user-facing "access levels" metric — used in role list metadata,
 * the team-page role dropdown, and the delete-role modal.
 */
export function countAccessLevels(permissions) {
  const { selections } = reverseResolvePermissions(
    Array.isArray(permissions) ? permissions : [],
  );
  return Object.values(selections).filter((l) => l && l !== 'none').length;
}

// ============================================================
// DRIFT GUARD — dev-mode assertion
// ============================================================
// Logs an error if a permission exists in the taxonomy but no matrix cell
// grants it. SETTINGS_PAYMENTS is non-delegatable (Superuser-only) and
// excluded by design.
if (process.env.NODE_ENV !== 'production') {
  const grantedByMatrix = new Set();
  for (const subItem of ALL_SUB_ITEMS) {
    for (const cell of Object.values(subItem.levels)) {
      cell.grants.forEach((p) => grantedByMatrix.add(p));
    }
  }
  const taxonomy = new Set(Object.values(PERMISSIONS));
  const ungrantable = [...taxonomy].filter(
    (p) =>
      !grantedByMatrix.has(p) &&
      p !== PERMISSIONS.SETTINGS_PAYMENTS &&
      p !== PERMISSIONS.SETTINGS_PAYMENTS_VIEW,
  );
  if (ungrantable.length) {
    // eslint-disable-next-line no-console
    console.error(
      '[MatrixDrift] Permissions exist but no matrix cell grants them:',
      ungrantable,
    );
  }
}
